import React, { useMemo } from 'react';

import './alarmModal.module.css';
import {
  InstalledProductResponse,
  orderData,
  getAlarmsColumns,
  AlarmModalContent
} from './shared';
import styles from './styles.module.css';
import BellIcon from '@/assets/icons/bell.svg?react';
import DataTable from '@/components/DataTable';
import EmptyState from '@/components/EmptyState';
import { ALARM_TYPES } from '@/constants/turbines';
import useModal from '@/context/ModalContext/useModal';
import { useToast } from '@/context/ToastContext';
import { useAlarmsAcknowledge } from '@/hooks/services/Turbines';
import { AlarmData } from '@/slices/types';
import { pulseColors } from '@/solaris-lens-common-lib/src/styles/colors';

interface AlarmsContentProps {
  installedProductData?: InstalledProductResponse;
  installedProductError?: Error | null;
  turbineId?: string;
}

export default function AlarmsContent({
  installedProductData,
  installedProductError = null,
  turbineId
}: AlarmsContentProps) {
  const { updateModalContent, closeModal } = useModal();
  const { showToast } = useToast();
  const acknowledgeMutation = useAlarmsAcknowledge();

  const alarms = useMemo(() => {
    if (!installedProductData) return [];

    const info = installedProductData.data?.info || installedProductData.info;
    if (!info) return [];

    const alarmsDataWarning =
      info.alarms?.map((alarm: AlarmData, index: number) => ({
        ...alarm,
        id: `warning-${index}`,
        name: alarm.label || alarm.type || alarm.name || '',
        description: alarm.description,
        date: alarm.startDate || alarm.date,
        startDate: alarm.startDate,
        acknowledged: Boolean(alarm.ackBy || alarm.ackAt),
        duration: alarm.duration,
        type: ALARM_TYPES.WARNING,
        tag: alarm.type || alarm.tag
      })) || [];

    const alarmsDataAlert =
      info.shutdowns?.map((alarm: AlarmData, index: number) => ({
        ...alarm,
        id: `alert-${index}`,
        name: alarm.label || alarm.type || alarm.name || '',
        description: alarm.description,
        date: alarm.startDate || alarm.date,
        startDate: alarm.startDate,
        acknowledged: Boolean(alarm.ackBy || alarm.ackAt),
        duration: alarm.duration,
        type: ALARM_TYPES.ALERT,
        tag: alarm.type || alarm.tag
      })) || [];

    return orderData([...alarmsDataWarning, ...alarmsDataAlert]);
  }, [installedProductData]);

  const handleDetailsClick = (alarm: AlarmData) => {
    const modalContent = (
      <AlarmModalContent alarm={alarm} onClose={closeModal} />
    );

    updateModalContent(modalContent, {
      modalTitle: 'Alarm Description',
      modalIcon: BellIcon,
      className: 'alarmsModal'
    });
  };

  const handleAcknowledge = async (tag: string) => {
    if (!turbineId || !tag) {
      showToast('Missing required information to acknowledge alarm', {
        type: 'error',
        autoClose: 5000
      });
      return;
    }

    acknowledgeMutation.mutate(
      { turbineId, tag: [tag] },
      {
        onSuccess: () => {
          showToast('Alarm acknowledged successfully', {
            type: 'success',
            autoClose: 3000
          });
        },
        onError: (error: any) => {
          showToast(
            error?.message || 'Failed to acknowledge alarm. Please try again.',
            {
              type: 'error',
              autoClose: 5000
            }
          );
        }
      }
    );
  };

  const columns = useMemo(
    () => getAlarmsColumns(handleDetailsClick, handleAcknowledge),
    [handleDetailsClick, handleAcknowledge]
  );

  if (installedProductError || !installedProductData || alarms.length === 0) {
    return (
      <EmptyState
        title="No data available"
        subtitle="No data available for this tab."
      />
    );
  }

  const getRowStyle = (row: AlarmData) => {
    const statusBarColors = {
      [ALARM_TYPES.ALERT]: pulseColors.red2,
      [ALARM_TYPES.WARNING]: pulseColors.warning,
      AlertAndAcknowledged: pulseColors.lightRed3,
      WarningAndAcknowledged: pulseColors.lightWarning4
    };

    const getStatusBarColor = () => {
      if (row.type === ALARM_TYPES.ALERT) {
        if (row.acknowledged) {
          return statusBarColors.AlertAndAcknowledged;
        }
        return statusBarColors[ALARM_TYPES.ALERT];
      }
      if (row.type === ALARM_TYPES.WARNING) {
        if (row.acknowledged) {
          return statusBarColors.WarningAndAcknowledged;
        }
        return statusBarColors[ALARM_TYPES.WARNING];
      }
      return '';
    };

    const statusBarColor = getStatusBarColor();
    const style: React.CSSProperties = {
      borderLeft: statusBarColor ? `4px solid ${statusBarColor}` : 'none'
    };

    if (row.acknowledged) {
      style.backgroundColor = pulseColors.gray6 || pulseColors.gray9;
    } else if (row.type === ALARM_TYPES.ALERT) {
      style.backgroundColor = pulseColors.lightRed2;
    } else if (row.type === ALARM_TYPES.WARNING) {
      style.backgroundColor = pulseColors.lightYellow;
    }

    return style;
  };

  return (
    <div className={`${styles.alarmsContainer}`}>
      <DataTable
        rows={alarms}
        columns={columns}
        getRowStyle={(row: AlarmData) => getRowStyle(row)}
        className={styles.alarmsTable}
        initialState={{
          sorting: {
            field: 'date',
            sort: 'desc'
          }
        }}
      />
    </div>
  );
}
